import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Search, SlidersHorizontal, X } from "react-feather";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { getTenantsListAPI } from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./TenantList.module.scss";

// ─── localStorage helpers ─────────────────────────────────────────────────────
const RECENT_KEY = "mh_recent_tenants";
const DRAFT_FILTERS_KEY = "mh_tenant_saved_filters";

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}
function pushRecent(tenant) {
  const cur = readRecent().filter((x) => x.id !== tenant.id);
  cur.unshift({
    id: tenant.id,
    label: `${tenant.tenantCode} — ${tenant.companyName}`,
  });
  localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 5)));
}

const STATUS_OPTIONS = ["active", "trial", "suspended", "at_risk"];
const PLAN_OPTIONS = ["Starter", "Growth", "Enterprise"];
const REGION_OPTIONS = ["MENA", "EU", "NA", "APAC"];

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cls =
    {
      active: styles.statusBadgeActive,
      trial: styles.statusBadgeTrial,
      suspended: styles.statusBadgeSuspended,
      at_risk: styles.statusBadgeAtRisk,
    }[status] || styles.statusBadgeTrial;
  return (
    <span className={`${styles.statusBadge} ${cls}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// ─── Onboarding progress ──────────────────────────────────────────────────────
function OnboardingBar({ step, total = 6 }) {
  if (step >= total)
    return <span className={styles.progressComplete}>Complete</span>;
  return (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${(step / total) * 100}%` }}
        />
      </div>
      <span className={styles.progressLabel}>
        {step}/{total}
      </span>
    </div>
  );
}

// ─── Row kebab menu ───────────────────────────────────────────────────────────
function KebabMenu({ row, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const items = [
    {
      label: "Open",
      onClick: () => navigate(platformUrl(`tenants/${row.id}`)),
    },
    {
      label: "Suspend",
      onClick: () => toast.info("Suspend "),
      danger: true,
    },
    {
      label: "Reset onboarding",
      onClick: () => toast.info("Reset onboarding"),
    },
    {
      label: "Support access",
      onClick: () => navigate(platformUrl(`tenants/${row.id}/support`)),
    },
    {
      label: "Resend invite",
      onClick: () => toast.success("Invite resent"),
    },
  ];

  return (
    <div
      className={styles.kebabWrap}
      ref={ref}
      onClick={(e) => e.stopPropagation()}
    >
      <button className={styles.kebabBtn} onClick={() => setOpen((o) => !o)}>
        ⋮
      </button>
      {open && (
        <div className={styles.kebabMenu}>
          {items.map((it) => (
            <button
              key={it.label}
              className={`${styles.kebabItem} ${it.danger ? styles.danger : ""}`}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filter drawer ────────────────────────────────────────────────────────────
function FilterDrawer({
  open,
  onClose,
  planSel,
  setPlanSel,
  statusSel,
  setStatusSel,
  region,
  setRegion,
  range,
  setRange,
  onApply,
}) {
  if (!open) return null;

  function togglePlan(p) {
    setPlanSel((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  }
  function toggleStatus(s) {
    setStatusSel((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    );
  }

  return (
    <div
      className={styles.drawerOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h2>Filters</h2>
          <button className={styles.drawerClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          <div className={styles.formGroup}>
            <label>Plan</label>
            <div className={styles.tagList}>
              {PLAN_OPTIONS.map((p) => (
                <button
                  key={p}
                  className={`${styles.tag} ${planSel.includes(p) ? styles.active : ""}`}
                  onClick={() => togglePlan(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Status</label>
            <div className={styles.tagList}>
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  className={`${styles.tag} ${statusSel.includes(s) ? styles.active : ""}`}
                  onClick={() => toggleStatus(s)}
                >
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Region</label>
            <select value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">Any region</option>
              {REGION_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Created range</label>
            <div className={styles.dateRow}>
              <DatePicker
                selected={range[0]}
                onChange={(d) => setRange([d, range[1]])}
                placeholderText="From"
              />
              <DatePicker
                selected={range[1]}
                onChange={(d) => setRange([range[0], d])}
                placeholderText="To"
              />
            </div>
          </div>
        </div>

        <div className={styles.drawerFooter}>
          <button
            className={styles.btnGhost}
            onClick={() => {
              setPlanSel([]);
              setStatusSel([]);
              setRegion("");
              setRange([null, null]);
            }}
          >
            Clear all
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              onApply();
              onClose();
            }}
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TenantList() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickStatus, setQuickStatus] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [planSel, setPlanSel] = useState([]);
  const [statusSel, setStatusSel] = useState([]);
  const [region, setRegion] = useState("");
  const [range, setRange] = useState([null, null]);
  const [search, setSearch] = useState("");
  const [recent, setRecent] = useState(readRecent);

  const savedFilters = useMemo(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(DRAFT_FILTERS_KEY) || "null") || [
          {
            id: "1",
            name: "At risk + Growth",
            plan: ["Growth"],
            status: ["at_risk"],
          },
          { id: "2", name: "Trials only", plan: [], status: ["trial"] },
          {
            id: "3",
            name: "Enterprise MENA",
            plan: ["Enterprise"],
            status: [],
            region: "MENA",
          },
        ]
      );
    } catch {
      return [];
    }
  }, []);

  const params = useMemo(() => {
    const p = { page: 1, limit: 100 };
    if (quickStatus) p.status = quickStatus;
    else if (statusSel.length) p.status = statusSel.join(",");
    if (planSel.length) p.plan = planSel.join(",");
    return p;
  }, [quickStatus, planSel, statusSel]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTenantsListAPI(params);
      let items = res.data.items || [];
      if (region) items = items.filter((t) => t.region === region);
      if (range[0] && range[1]) {
        const a = range[0].getTime(),
          b = range[1].getTime();
        items = items.filter((t) => {
          const c = new Date(t.createdAt).getTime();
          return c >= a && c <= b;
        });
      }
      setPayload({ ...res.data, items });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [params, region, range]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = payload?.summary || {
    active: 42,
    trial: 6,
    suspended: 3,
    atRisk: 2,
  };

  const filteredItems = useMemo(() => {
    if (!payload?.items) return [];
    if (!search.trim()) return payload.items;
    const q = search.toLowerCase();
    return payload.items.filter(
      (t) =>
        t.companyName?.toLowerCase().includes(q) ||
        t.tenantCode?.toLowerCase().includes(q) ||
        t.domain?.toLowerCase().includes(q),
    );
  }, [payload, search]);

  const toggleQuick = (val) => setQuickStatus((s) => (s === val ? "" : val));

  function handleRowClick(row) {
    pushRecent(row);
    setRecent(readRecent());
    navigate(platformUrl(`tenants/${row.id}`));
  }

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading && !payload) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Tenants"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Tenants" },
          ]}
        />
        <div className={styles.body}>
          <div className={styles.main}>
            <div className={styles.skeleton} style={{ height: 44 }} />
            <div className={styles.skeleton} style={{ height: 400 }} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Tenants"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Tenants" },
          ]}
        />
        <div className={styles.body}>
          <div className={styles.emptyState}>
            <AlertTriangle size={40} />
            <h3>Could not load tenants</h3>
            <p>{error.message}</p>
            <button className={styles.btnPrimary} onClick={load}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Tenants"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Tenants" },
        ]}
        primaryAction={{
          label: "Create tenant",
          onClick: () => navigate(platformUrl("tenants/create")),
        }}
        secondaryActions={[
          {
            label: "Export CSV",
            onClick: () => toast.success("Export started"),
          },
          { label: "Filters", onClick: () => setFilterOpen(true) },
        ]}
      />

      <div className={styles.body}>
        {/* ── Main column ─────────────────────────────────────────────── */}
        <div className={styles.main}>
          {/* Quick pills */}
          <div className={styles.quickBar}>
            <button
              className={`${styles.quickPill} ${quickStatus === "active" ? styles.active : ""}`}
              onClick={() => toggleQuick("active")}
            >
              Active <span className={styles.pillCount}>{summary.active}</span>
            </button>
            <button
              className={`${styles.quickPill} ${quickStatus === "trial" ? styles.active : ""}`}
              onClick={() => toggleQuick("trial")}
            >
              Trial <span className={styles.pillCount}>{summary.trial}</span>
            </button>
            <button
              className={`${styles.quickPill} ${styles.danger} ${quickStatus === "suspended" ? styles.active : ""}`}
              onClick={() => toggleQuick("suspended")}
            >
              Suspended{" "}
              <span className={styles.pillCount}>{summary.suspended}</span>
            </button>
            <button
              className={`${styles.quickPill} ${styles.warning} ${quickStatus === "at_risk" ? styles.active : ""}`}
              onClick={() => toggleQuick("at_risk")}
            >
              At risk <span className={styles.pillCount}>{summary.atRisk}</span>
            </button>
          </div>

          {/* Search */}
          <div className={styles.searchWrap}>
            <Search size={15} />
            <input
              placeholder="Search tenants…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Table card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Tenants</h2>
              <span className={styles.cardBadge}>
                {filteredItems.length} results
              </span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Company</th>
                    <th>Domain</th>
                    <th>Plan</th>
                    <th>Onboarding</th>
                    <th>Users</th>
                    <th>Last activity</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((r) => (
                    <tr
                      key={r.id}
                      className={
                        r.status === "suspended" ? styles.suspended : ""
                      }
                      onClick={() => handleRowClick(r)}
                    >
                      <td>
                        <span className={styles.tenantCode}>
                          {r.tenantCode}
                        </span>
                      </td>
                      <td>{r.companyName}</td>
                      <td>
                        <span className={styles.domainText}>{r.domain}</span>
                      </td>
                      <td>
                        <span className={styles.planBadge}>{r.plan}</span>
                      </td>
                      <td>
                        <OnboardingBar step={r.onboardingStep} />
                      </td>
                      <td>{r.totalUsers}</td>
                      <td>{new Date(r.lastActivity).toLocaleDateString()}</td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                      <td>
                        <KebabMenu row={r} navigate={navigate} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.totalCount}>
            Total: {payload.total} tenants
          </div>
        </div>

        {/* ── Rail sidebar ─────────────────────────────────────────────── */}
        <div className={styles.rail}>
          <div className={styles.railInner}>
            <div className={styles.railSection}>
              <div className={styles.railLabel}>Saved filters</div>
              {savedFilters.map((sf) => (
                <button
                  key={sf.id}
                  className={styles.savedFilterBtn}
                  onClick={() => {
                    setPlanSel(sf.plan || []);
                    setStatusSel(sf.status || []);
                    if (sf.region) setRegion(sf.region);
                    toast.info(`Applied: ${sf.name}`);
                  }}
                >
                  {sf.name}
                </button>
              ))}
            </div>

            <div className={styles.railSection}>
              <div className={styles.railLabel}>Recently viewed</div>
              <ul className={styles.recentList}>
                {recent.length === 0 && (
                  <li>
                    <span className={styles.recentEmpty}>None yet</span>
                  </li>
                )}
                {recent.map((x) => (
                  <li key={x.id} className={styles.recentItem}>
                    <button
                      className={styles.recentLink}
                      onClick={() => navigate(platformUrl(`tenants/${x.id}`))}
                    >
                      {x.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter drawer ─────────────────────────────────────────────── */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        planSel={planSel}
        setPlanSel={setPlanSel}
        statusSel={statusSel}
        setStatusSel={setStatusSel}
        region={region}
        setRegion={setRegion}
        range={range}
        setRange={setRange}
        onApply={load}
      />
    </div>
  );
}
